// Use relative URL to go through Vite's proxy in development
// In production, set VITE_API_URL to the actual API server URL
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

const TOKEN_KEY = 'auth_token';

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// Token refresh callback - set by AuthContext
let tokenRefreshCallback: (() => Promise<string | null>) | null = null;

/**
 * Set the token refresh callback (called by AuthContext)
 */
export const setTokenRefreshCallback = (
  callback: (() => Promise<string | null>) | null
): void => {
  tokenRefreshCallback = callback;
};

/**
 * Get stored auth token
 */
export const getAuthToken = (): string | null => {
  return globalThis.localStorage.getItem(TOKEN_KEY);
};

/**
 * Store auth token
 */
export const setAuthToken = (token: string): void => {
  globalThis.localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove auth token
 */
export const removeAuthToken = (): void => {
  globalThis.localStorage.removeItem(TOKEN_KEY);
};

export class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getHeaders(): globalThis.HeadersInit {
    const headers: globalThis.HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Only add ngrok header for local ngrok development
    if (window.location.hostname.includes('ngrok')) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }

    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private async handleResponse<T>(
    response: globalThis.Response,
    retryFn?: () => Promise<T>
  ): Promise<T> {
    if (!response.ok) {
      let error: ApiError;
      try {
        const text = await response.text();
        // Try to parse as JSON, fall back to plain text error
        try {
          error = JSON.parse(text);
        } catch {
          error = {
            message: text || 'An error occurred',
            statusCode: response.status,
          };
        }
      } catch {
        error = {
          message: 'An error occurred',
          statusCode: response.status,
        };
      }

      // Handle 401 Unauthorized - try to refresh token
      if (response.status === 401 && tokenRefreshCallback && retryFn) {
        try {
          console.log('401 received, attempting token refresh...');
          const newToken = await tokenRefreshCallback();
          if (newToken) {
            setAuthToken(newToken);
            // Retry the original request with new token
            return retryFn();
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
        // If refresh failed, clear token
        removeAuthToken();
      } else if (response.status === 401) {
        removeAuthToken();
      }

      throw error;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // Parse JSON response safely
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }
    try {
      return JSON.parse(text) as T;
    } catch {
      console.error(
        'Failed to parse response as JSON:',
        text.substring(0, 200)
      );
      throw new Error('Invalid response from server');
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    const makeRequest = async (): Promise<T> => {
      const response = await globalThis.fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers: this.getHeaders(),
      });
      return this.handleResponse<T>(response, makeRequest);
    };
    return makeRequest();
  }

  async post<T>(endpoint: string, data: unknown): Promise<T> {
    const makeRequest = async (): Promise<T> => {
      const response = await globalThis.fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, makeRequest);
    };
    return makeRequest();
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    const makeRequest = async (): Promise<T> => {
      const response = await globalThis.fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, makeRequest);
    };
    return makeRequest();
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    const makeRequest = async (): Promise<T> => {
      const response = await globalThis.fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, makeRequest);
    };
    return makeRequest();
  }

  async delete<T>(endpoint: string): Promise<T> {
    const makeRequest = async (): Promise<T> => {
      const response = await globalThis.fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers: this.getHeaders(),
      });
      return this.handleResponse<T>(response, makeRequest);
    };
    return makeRequest();
  }
}

export const apiService = new ApiService(API_BASE_URL);

// Alias for cleaner imports
export const apiClient = apiService;
